import { motion } from "framer-motion";
import leopardImg from "../../accests/yala/leopardImg.jfif";
import elephantImg from "../../accests/yala/elephantImg.jfif";
import hero from "../../accests/yala/heroImg.jfif";
import yala from "../../accests/yala/yaala (1).jpeg";
import yala1 from "../../accests/yala/yaala (2).jpeg";
import yala2 from "../../accests/yala/yaala (4).jpeg";

export default function YalaDayTour() {

    const highlights = [
        {
            title: "Yala National Park Safari",
            desc: "Embark on an exhilarating 4×4 safari through Sri Lanka’s most famous wildlife reserve, home to leopards, elephants, sloth bears, and crocodiles.",
            img: hero,
        },
        {
            title: "Leopard Spotting",
            desc: "Yala boasts one of the highest leopard densities in the world, offering rare opportunities to spot these elusive big cats in the wild.",
            img: leopardImg,
        },
        {
            title: "Elephants & Birdlife",
            desc: "Witness herds of Asian elephants and over 200 bird species across lagoons, grasslands, and forest landscapes.",
            img: elephantImg,
        },
    ];

    const itinerary = [
        "Early morning pickup from your hotel",
        "Scenic drive with refreshment break",
        "Lunch stop at Tissamaharama",
        "4×4 jeep safari at Yala National Park",
        "Spot leopards, elephants, sloth bears & birdlife",
        "Photography & short relaxation breaks",
        "Return journey to your hotel",
    ];

    return (
        <div className="pt-28 bg-gray-50">

            {/* HERO */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="relative h-[65vh]"
            >
                <img
                    src={yala}
                    alt="Yala National Park Safari"
                    className="w-full h-full object-cover brightness-75"
                />

                <div className="absolute inset-0 flex flex-col justify-center items-center text-center text-white px-6">
                    <motion.h1
                        initial={{ y: -40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 1, delay: 0.3 }}
                        className="text-5xl md:text-6xl font-bold drop-shadow-lg"
                    >
                        Yala Day Tour
                    </motion.h1>

                    <motion.p
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 1, delay: 0.6 }}
                        className="mt-4 max-w-3xl text-lg drop-shadow-md"
                    >
                        Discover Sri Lanka’s premier wildlife destination and
                        experience an unforgettable safari adventure.
                    </motion.p>
                </div>
            </motion.div>

            {/* INTRO */}
            <section className="max-w-5xl mx-auto px-6 py-16 text-center">
                <h2 className="text-3xl font-bold mb-6 text-gray-800">
                    Sri Lanka’s Premier Wildlife Haven
                </h2>
                <p className="text-gray-700 leading-relaxed">
                    Yala National Park is world-famous for its incredible biodiversity
                    and high leopard density. From vast grasslands to coastal lagoons,
                    the park offers unforgettable wildlife encounters and breathtaking
                    natural scenery.
                </p>
            </section>

            {/* HIGHLIGHTS */}
            <section data-aos="fade-up"
                     data-aos-duration="3000" className="max-w-7xl mx-auto px-6 py-16">
                <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">
                    Tour Highlights
                </h2>

                <div className="grid md:grid-cols-3 gap-8">
                    {highlights.map((item, index) => (
                        <motion.div
                            key={index}
                            whileHover={{ scale: 1.05 }}
                            className="bg-white rounded-3xl shadow-lg overflow-hidden"
                        >
                            <img
                                src={item.img}
                                alt={item.title}
                                className="w-full h-52 object-cover"
                            />
                            <div className="p-6">
                                <h3 className="text-xl font-semibold text-yellow-500 mb-2">
                                    {item.title}
                                </h3>
                                <p className="text-gray-600 text-sm">{item.desc}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ITINERARY */}
            <section data-aos="fade-up"
                     data-aos-duration="3000" className="bg-gray-100 py-16">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">

                    {/* LEFT */}
                    <div>
                        <h2 className="text-3xl font-bold mb-10 text-gray-800">
                            Detailed Itinerary
                        </h2>

                        <ul className="space-y-4 text-gray-700 list-decimal list-inside">
                            {itinerary.map((step, index) => (
                                <motion.li
                                    key={index}
                                    initial={{ x: -40, opacity: 0 }}
                                    whileInView={{ x: 0, opacity: 1 }}
                                    transition={{ duration: 0.4, delay: index * 0.1 }}
                                    className="text-lg"
                                >
                                    {step}
                                </motion.li>
                            ))}
                        </ul>
                    </div>

                    {/* RIGHT IMAGES */}
                    <div data-aos="fade-up"
                         data-aos-duration="3000" className="grid grid-cols-2 gap-4">
                        {[yala, yala1, yala2, hero].map((img, i) => (
                            <motion.img
                                key={i}
                                src={img}
                                alt="Yala safari"
                                whileHover={{ scale: 1.05 }}
                                className="h-40 w-full object-cover rounded-2xl shadow-lg"
                            />
                        ))}
                    </div>

                </div>
            </section>

            {/* WHY CHOOSE + CTA */}
            <section data-aos="fade-up"
                     data-aos-duration="3000" className="relative py-24 overflow-hidden">

                {/* BACKGROUND */}
                <div className="absolute inset-0">
                    <img
                        src={hero}
                        alt="Why choose Yala tour"
                        className="w-full h-full object-cover scale-110 blur-md"
                    />
                    <div className="absolute inset-0 bg-black/60"></div>
                </div>

                {/* CONTENT */}
                <div  data-aos="fade-up"
                      data-aos-duration="3000" className="relative max-w-6xl mx-auto px-6 text-white text-center">

                    <h2 className="text-3xl md:text-4xl font-bold mb-10">
                        Why Choose This Tour?
                    </h2>

                    <ul className="max-w-3xl mx-auto space-y-4 text-lg list-disc list-inside
                                   backdrop-blur-md bg-white/10 p-8 rounded-3xl shadow-xl">
                        <li>Visit Sri Lanka’s most famous wildlife national park.</li>
                        <li>One of the highest leopard densities in the world.</li>
                        <li>Thrilling 4×4 jeep safari experience.</li>
                        <li>Perfect for wildlife photography and nature lovers.</li>
                        <li>Guided by experienced local safari experts.</li>
                    </ul>

                    {/* CTA */}
                    {/* CTA */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="mt-14"
                    >
                        <motion.a
                            whileHover={{ scale: 1.08 }}
                            whileTap={{ scale: 0.95 }}
                            href="https://wa.me/94770163670?text=Hello%20I%20would%20like%20to%20book%20the%20Yala%20Day%20Tour"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center px-14 py-4
        bg-yellow-500 text-black font-semibold text-lg
        rounded-full shadow-xl hover:bg-yellow-400
        transition-all duration-300"
                        >
                            Book Yala Day Tour
                        </motion.a>

                        <p className="mt-4 text-sm text-gray-200">
                            🐆 Leopard sightings • 🚙 Jeep safari • 📸 Photo opportunities
                        </p>
                    </motion.div>

                </div>
            </section>

        </div>
    );
}
